import React, { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { Dropdown, DropdownButton, Form } from 'react-bootstrap';
import { diffMethodArray } from './utils';
import { UiIcon } from '@components/common/icons/ui-icon';
import {
  ArrowsAngleContract,
  ArrowsAngleExpand,
  CheckCircleFill,
  DashCircle,
  PlusCircle,
  XCircleFill
} from 'react-bootstrap-icons';
import { VersionIndexType } from './revision-modal';
import { useApplicationState } from '@hooks/common/use-application-state';
import { IconButton } from '@components/common/icon-button/icon-button';
import { useUpdateDraftStatusMutation } from '@services/versionHistoryService';
import { useSingleStringUrlParameter } from '@hooks/common/use-single-string-url-parameter';
import { useUiNotifications } from '@components/notifications/ui-notification-boundary';
import { useDispatch } from 'react-redux';
import { setNoteContent } from '@redux/note-details/methods';
import { setDraftContent } from '@redux/note-draft/noteDraft';
import { ArticlePublishConfirmationModal } from '@components/layout/publish';
import { ShowIf } from '@components/common/show-if/show-if';
import { useRevalidateArticle } from '@hooks/common/use-revalidate-article';

type RevisionModalSubHeaderProps = {
  setCompareMethod: Function;
  setIsSplitView: Function;
  isSplitView: boolean;
  compareMethod: string;
  setFullscreen: Function;
  fullscreen: true | string | undefined; // should be true or undefined here
  setLeftSideToggle: Function;
  leftSideToggle: boolean;
  selectedVersionIndex: VersionIndexType | undefined;
  isDraft: boolean;
  onHide: () => void
};

const RevisionModalSubHeader = ({
  setCompareMethod,
  setIsSplitView,
  isSplitView,
  compareMethod,
  setFullscreen,
  fullscreen,
  setLeftSideToggle,
  leftSideToggle,
  selectedVersionIndex,
  isDraft,
  onHide,
}: RevisionModalSubHeaderProps) => {
  const isFullScreen = useMemo(() => (fullscreen ? true : false), [fullscreen]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { draftStatus, draftContent } = useApplicationState((state) => ({
    draftStatus: state.noteDraft.status,
    draftContent: state.noteDraft.content
  }));
  const noteIdOrAlias = useSingleStringUrlParameter('noteId', '');
  const [updateDraftStatus, {isLoading: isDraftStatusLoading}] = useUpdateDraftStatusMutation();
  const { dispatchUiNotification } = useUiNotifications();
  const dispatch = useDispatch();
  const { revalidateArticleCallback } = useRevalidateArticle(['', noteIdOrAlias]); // '' empty to revalidate home page

  const handleDraftStatus = useCallback((status: 'approved' | 'rejected') => {
    updateDraftStatus({
      noteIdOrAlias: noteIdOrAlias || '',
      draftStatus: status,
    }).unwrap().then((res) => {
      if (res.success) {
        dispatchUiNotification(
          'Draft Status Update',
          `Draft ${status} successfully`,
          {}
        );
        if (status === 'approved') {
          setNoteContent(draftContent);
          dispatch(setDraftContent(''))
          revalidateArticleCallback();
          onHide();
        }
      } else {
        dispatchUiNotification(
          'Draft Status',
          'Error while updating draft',
          {}
        );
      }
    })
    .catch((err) => {
      dispatchUiNotification(
        'Draft Status Update Error',
        err.message || err.data.error,
        {}
      );
    });
  }, [noteIdOrAlias]);

  return (
    <div className='justify-content-between d-flex p-2 px-3'>
      <div>
        {selectedVersionIndex !== undefined && !isDraft ? (
          <div
            className='cursor-pointer'
            onClick={() => setLeftSideToggle(!leftSideToggle)}>
            <UiIcon icon={!leftSideToggle ? PlusCircle : DashCircle} />
          </div>
        ) : null}
        {isDraft ? (
          <>
            Draft Status: <span className={cn("text-capitalize", {
              'text-danger': draftStatus === 'rejected',
              'text-success': draftStatus === 'approved',
              'text-primary': draftStatus === 'pending',
            })}>{(draftStatus || '')}</span>
          </>
          ) : null}
      </div>
      <div className='d-flex gap-5'>
        <div>
          <DropdownButton title='Compare Method'>
            {diffMethodArray.map((method, index) => {
              return (
                <Dropdown.Item
                  key={`method-${index}`}
                  active={method === compareMethod}
                  onClick={() => setCompareMethod(method)}>
                  {method}
                </Dropdown.Item>
              );
            })}
          </DropdownButton>
        </div>
        <div>
          <Form>
            <Form.Check
              checked={isSplitView}
              type='switch'
              label='Split View'
              onChange={(e) => setIsSplitView(!isSplitView)}
              className='cursor-pointer'
            />
          </Form>
        </div>
      </div>
      <div className='d-flex align-items-center justify-content-between gap-4'>
        {isDraft && draftStatus === 'pending' ? (
          <div className='d-flex align-items-center justify-content-between gap-2'>
            <IconButton
              icon={XCircleFill}
              className='bg-danger border-danger'
              border
              onClick={() => handleDraftStatus('rejected')}
              disabled={isDraftStatusLoading}>
              Reject
            </IconButton>
            <IconButton
              icon={CheckCircleFill}
              border
              className='bg-success border-success'
              onClick={() => setShowConfirmation(true)}
              disabled={isDraftStatusLoading}>
              Approve
            </IconButton>
          </div>
        ) : null}
        <div
          className='cursor-pointer d-flex align-items-center'
          onClick={() => setFullscreen(!isFullScreen)}>
          <UiIcon icon={fullscreen ? ArrowsAngleContract : ArrowsAngleExpand} />
        </div>
      </div>
      <ShowIf condition={isDraft}>
        <ArticlePublishConfirmationModal
          onConfirm={() => handleDraftStatus('approved')}
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
        />
      </ShowIf>
    </div>
  );
};

export default RevisionModalSubHeader;
